import type { ReactNode } from 'react';
import { FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, FilterPopover, PageHeader, SectionCard } from '@/components/ui';
import { AccountsFacetRail } from './AccountsFacetRail';
import {
    AccountsToolbar,
    type AccountsPlacement,
    type AccountsToolbarProps,
} from './AccountsToolbar';
import type { FacetGroup } from './facetTypes';

export type AccountsTab = 'accounts' | 'audit';

/**
 * EAA-1839 (frame 35 tab row): one entry in the tab strip beyond the two the
 * screen always has ("Tài khoản" and "Nhật ký định danh").
 *
 * `panel` is the discriminator that matters: an entry WITHOUT one is a saved view
 * over the same table (frame 35's "Chưa có Level 42" and "Lời mời 18" — the tab
 * click sets filters, the board stays), while an entry WITH one replaces the board
 * entirely. Getting this backwards was the temptation: modelling "Chưa có Level"
 * as its own panel would have meant a second table, a second selection model and a
 * second bulk bar for a view that differs from the main one by exactly one query
 * param.
 */
export interface AccountsExtraTab<Tab extends string> {
    key: Tab;
    label: string;
    /** `null` = not counted yet; omitted = this tab shows no count. */
    count?: number | null;
    /** Paints the count badge in the warning tone (frame 35: "Chưa có Level 42"). */
    tone?: 'warn';
    /** Present ⇒ replaces the list board while this tab is active. */
    panel?: ReactNode;
}

export interface AccountsScreenProps<Tab extends string = AccountsTab> {
    context: 'realm' | 'org';
    /**
     * EAA-2068: rendered INSIDE another page (the app-detail "Tài khoản" tab)
     * rather than as a page of its own. Drops the page header and this screen's
     * tab strip (the host page owns both), renders a `<div>` instead of a second
     * `<main>`, and moves `headerActions` into the toolbar's actions slot so
     * "Tạo tài khoản" is still reachable. Everything below the tabs — rail,
     * toolbar, body — is unchanged.
     */
    embedded?: boolean;
    title: string;
    subtitle: string;
    /**
     * EAA-1839 (frames 35 + 35g): `true` prints the subtitle as a line inside the
     * title strip. Default `false` collapses it into the (i) beside the title, which
     * IS frame 35g — the title strip carries a title and an (i), and the page
     * description lives one hover away. Frame 35 was drawn that way after the
     * printed line pushed the tab row down far enough to overlap the board.
     */
    printSubtitle?: boolean;
    headerActions?: ReactNode;

    tab: Tab;
    onTabChange: (tab: Tab) => void;
    /** Count beside the always-present "Tài khoản" tab. `undefined` renders none. */
    accountsTabCount?: number | null;
    showAuditTab: boolean;
    auditPanel: ReactNode;
    /** Inserted between "Tài khoản" and "Nhật ký định danh" — see `AccountsExtraTab`. */
    extraTabs?: AccountsExtraTab<Tab>[];


    facetGroups: FacetGroup[];
    onSelectFacet: (groupKey: string, optionKey: string) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    /** Frame 35's "Lọc nhanh trong danh mục…" box above the rail groups. */
    railQuickFilter?: { value: string; onChange: (value: string) => void };
    /** Frame 35's closing line under the rail ("Bộ lọc cộng dồn…"). */
    railNote?: string;
    /**
     * EAA-1839 (2026-08-21, user request, ADDITION beyond frame 35 — frame 35
     * draws ONLY the sidebar rail): where the facet rail renders. Omitted ⇒
     * `'sidebar'`, i.e. today's grid layout, unchanged — so `AccountsPage.tsx`
     * (realm), which does not pass this, is untouched. When the caller passes
     * `'header'`, `.acc-board` drops to a single full-width column (the rail
     * leaves the grid entirely rather than leaving an empty 236px gutter) and
     * a collapsed "Bộ lọc (N)" trigger renders inside the toolbar's own
     * row, right after the search input (`AccountsToolbar`'s `filterTrigger`
     * prop — EAA-1839, 2026-08-21, user request; it used to be its own row
     * above the toolbar, `.acc-filterbar`, before the user asked for it moved
     * in), opening the SAME `AccountsFacetRail` inside a `FilterPopover` panel
     * — see that component's `variant` prop. The control that flips this
     * value lives inside the toolbar's "Hiển thị" popover
     * (`AccountsToolbar`'s `placement` prop below), not as its own top-level
     * button, because the toolbar row has no room left for a fourth top-level
     * control.
     */
    placement?: { value: AccountsPlacement; onChange: (value: AccountsPlacement) => void };

    search: string;
    onSearchChange: (value: string) => void;
    searchDisabledReason?: string;
    activeFilterCount: number;
    /** Passed straight through to the toolbar — see `AccountsToolbarProps`.
     *  EAA-1839: widened (additive, backward-compatible) to also carry the
     *  search box's placeholder/aria-label/cap-note overrides — the org screen
     *  needs its own wording (it searches only email/sub, not name/username)
     *  and there was no other seam to deliver it through without inventing a
     *  parallel prop path. */
    toolbarExtras?: Pick<
        AccountsToolbarProps,
        | 'columns'
        | 'density'
        | 'onRefresh'
        | 'refreshBusy'
        | 'searchPlaceholder'
        | 'searchAriaLabel'
        | 'searchCapNote'
    >;

    /** Rendered below the toolbar: bulk bar (if any selection) + table/pagination,
     *  OR a loading/error/empty state — each page owns which of those applies. */
    body: ReactNode;
}

/**
 * EAA-1820: the ONE accounts screen — replaces `AccountsPage.tsx` (realm) and
 * `ScopedAccountsPage.tsx` (org)'s duplicated layout. Per the design pack's own
 * README (the governing rule this ticket implements): "Danh sách tài khoản ở tổ
 * chức và ở realm dùng chung một UI: cùng tab, bộ lọc đã lưu, rail bộ lọc, bảng và
 * thanh thao tác. Khác nhau chỉ ở ngữ cảnh."
 *
 * What is genuinely context-specific stays OUTSIDE this component and is passed
 * in: the write dialogs (issue/reset/delete for realm;
 * add/revoke/reset + bulk Level-assign for org — neither belongs to a shared screen
 * since their backends and permissions differ entirely), and the `body` slot's
 * table columns/bulk actions (built from `shared/AccountsTable` +
 * `shared/AccountsBulkBar` + `shared/AccountsPagination` by each page, since the two
 * row shapes — a Keycloak account vs. an Assignment grant — are different entities
 * with different columns).
 *
 * EAA-1839 (frame 35 completion pass): the tab strip stopped being the hard-coded
 * pair `accounts | audit`. Frame 35 draws four tabs, two of which are saved views
 * over the SAME board, so `Tab` is now a type parameter and `extraTabs` carries
 * them; the realm page keeps passing its `'accounts' | 'audit'` union and infers
 * exactly that, unchanged.
 *
 * TWO OF FRAME 35's ROWS ARE DELIBERATELY NOT BUILT, and both were built first and
 * then removed on 2026-08-21 at the user's request. Neither should be rebuilt from
 * the design without asking.
 *   - The saved-filter preset row. See `useScopedAccountViews`'s docstring: this is
 *     the second removal of that row, the first being 2026-08-04 on the realm board.
 *   - The "Phạm vi" scope band, which this component used to render above the board
 *     and then briefly inside the rail. It is redundant with the "Đơn vị" facet
 *     group, whose first option IS the scope org alone — `child_organization_id`
 *     replaces the effective scope rather than widening it, so picking that option
 *     is exactly what the band's "Chỉ tài khoản trực thuộc" toggle did. Two controls
 *     for one query param is how a screen starts disagreeing with itself.
 *
 * Both removals were also about vertical space: every row between the tabs and the
 * board shortens the table by its own height on every viewport.
 *
 * FIT-TO-SHELL (frame 69): `portal-console-page--fit acc-screen` — title, tabs,
 * then a growing board (facet rail + table card). No outer SectionCard titled
 * "Danh sách tài khoản"; only `.acc-table-scroll` scrolls. Every row that used to
 * sit between the tabs and the board (the saved-filter row, the scope band) has
 * been removed or moved into the rail, because each one shortened the table by its
 * own height on every viewport.
 */
export function AccountsScreen<Tab extends string = AccountsTab>({
    embedded = false,
    title,
    subtitle,
    printSubtitle = false,
    headerActions,
    tab,
    onTabChange,
    accountsTabCount,
    showAuditTab,
    auditPanel,
    extraTabs,
    facetGroups,
    onSelectFacet,
    hasActiveFilters,
    onClearFilters,
    railQuickFilter,
    railNote,
    placement,
    search,
    onSearchChange,
    searchDisabledReason,
    activeFilterCount,
    toolbarExtras,
    body,
}: AccountsScreenProps<Tab>) {
    const { t } = useTranslation();

    // EAA-1839: default stays `'sidebar'` — the ONLY layout frame 35 draws — so a
    // caller that never passes `placement` (the realm `AccountsPage.tsx`) renders
    // byte-for-byte what it did before this prop existed.
    const placementValue: AccountsPlacement = placement?.value ?? 'sidebar';
    const activeExtra = embedded ? undefined : extraTabs?.find((entry) => entry.key === tab);
    const showAuditPanel = !embedded && tab === ('audit' as Tab) && showAuditTab;
    // A tab with its own `panel` takes over the board; a saved-view tab does not.
    const showList = !showAuditPanel && !activeExtra?.panel;

    function tabButton(key: Tab, label: string, count?: number | null, tone?: 'warn') {
        return (
            <button
                key={key}
                type="button"
                role="tab"
                id={`acc-tab-${key}`}
                aria-selected={tab === key}
                aria-controls={`acc-panel-${key}`}
                className={'acc-tab' + (tab === key ? ' acc-tab--active' : '')}
                onClick={() => onTabChange(key)}
            >
                {label}
                {count !== undefined && (
                    <span
                        className={
                            'acc-tab__count' + (tone === 'warn' ? ' acc-tab__count--warn' : '')
                        }
                    >
                        {count === null ? '—' : count.toLocaleString('vi-VN')}
                    </span>
                )}
            </button>
        );
    }

    const Root = embedded ? 'div' : 'main';

    return (
        <Root
            className={
                embedded
                    ? 'portal-console-page--fit acc-screen acc-screen--embedded'
                    : 'portal-admin portal-console-page portal-console-page--fit acc-screen'
            }
        >
            {!embedded && (
                <PageHeader
                    title={title}
                    collapseSubtitle={!printSubtitle}
                    subtitle={subtitle}
                    actions={headerActions}
                    marginBottom={0}
                />
            )}

            {!embedded && (
                <div className="acc-tabs" role="tablist" aria-label={title}>
                    {tabButton('accounts' as Tab, t('console.accounts.page.title'), accountsTabCount)}
                    {extraTabs?.map((entry) =>
                        tabButton(entry.key, entry.label, entry.count, entry.tone)
                    )}
                    {showAuditTab &&
                        tabButton('audit' as Tab, t('console.accounts.page.tabAudit'))}
                </div>
            )}

            {showAuditPanel && (
                <div className="fit-grow" role="tabpanel" id="acc-panel-audit" aria-labelledby="acc-tab-audit">
                    <SectionCard title={t('console.accounts.page.tabAudit')}>{auditPanel}</SectionCard>
                </div>
            )}

            {activeExtra?.panel && (
                <div
                    className="fit-grow"
                    role="tabpanel"
                    id={`acc-panel-${activeExtra.key}`}
                    aria-labelledby={`acc-tab-${activeExtra.key}`}
                >
                    {activeExtra.panel}
                </div>
            )}

            {showList && (
                <div
                    role={embedded ? undefined : 'tabpanel'}
                    id={embedded ? undefined : 'acc-panel-accounts'}
                    aria-labelledby={embedded ? undefined : `acc-tab-${tab}`}
                    className="acc-screen-list"
                >

                    <div className="fit-grow acc-screen-board">
                        <div
                            className={
                                'acc-board' +
                                (placementValue === 'header' ? ' acc-board--header' : '')
                            }
                        >
                            {placementValue === 'sidebar' && (
                                <AccountsFacetRail
                                    groups={facetGroups}
                                    onSelect={onSelectFacet}
                                    hasActiveFilters={hasActiveFilters}
                                    onClear={onClearFilters}
                                    quickFilter={railQuickFilter}
                                    note={railNote}
                                />
                            )}
                            <div className="acc-board-content">
                                <AccountsToolbar
                                    search={search}
                                    onSearchChange={onSearchChange}
                                    searchDisabledReason={searchDisabledReason}
                                    activeFilterCount={activeFilterCount}
                                    onClearFilters={onClearFilters}
                                    placement={placement}
                                    // EAA-1839 (2026-08-21, user request): the rail's
                                    // replacement when placement is "Trên bảng" used to
                                    // render on its OWN row above this toolbar
                                    // (`.acc-filterbar`) — the user asked for it in the
                                    // same row as the search input instead, so it is now
                                    // handed through `filterTrigger` and rendered inside
                                    // the toolbar's own flex row. Measured at 1366/1440/
                                    // 1600px: the row still does not wrap (see this
                                    // ticket's verification notes), so nothing else had
                                    // to give to make room for it.
                                    filterTrigger={
                                        placementValue === 'header' && (
                                            <FilterPopover
                                                align="start"
                                                width={280}
                                                panelClassName="acc-filterbar__panel"
                                                trigger={({ open, ref, toggle }) => (
                                                    <Button
                                                        ref={ref}
                                                        icon={<FilterOutlined />}
                                                        aria-expanded={open}
                                                        aria-label={t(
                                                            'console.accounts.filters.filterTriggerAriaLabel',
                                                            { count: activeFilterCount }
                                                        )}
                                                        onClick={toggle}
                                                    >
                                                        {t(
                                                            'console.accounts.filters.filterTriggerButton',
                                                            { count: activeFilterCount }
                                                        )}
                                                    </Button>
                                                )}
                                            >
                                                <AccountsFacetRail
                                                    variant="popover"
                                                    groups={facetGroups}
                                                    onSelect={onSelectFacet}
                                                    hasActiveFilters={hasActiveFilters}
                                                    onClear={onClearFilters}
                                                    quickFilter={railQuickFilter}
                                                    note={railNote}
                                                />
                                            </FilterPopover>
                                        )
                                    }
                                    actions={embedded ? headerActions || undefined : undefined}
                                    {...toolbarExtras}
                                />
                                <div className="acc-board-body">{body}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Root>
    );
}

export default AccountsScreen;
